import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { Product } from "types";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { getAuthCookie } from "../../utils/authCookies";

type FileWithPreview = {
  file: File;
  preview: string;
};

interface License {
  id: string;
  firstName: string;
  lastName: string;
  statuss: string;
  licenseType: string;
  offices: string[] | null;
}

type Office = {
  id: string;
  officeName: string;
  officeType: string;
  officeAddress: OfficeAddress;
};

type OfficeAddress = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

type Brand = {
  id: string;
  name: string;
  image: string;
  status: string;
};

type Category = {
  id: string;
  name: string;
  image: string;
  status: string;
};

type OfficeInventoryObj = {
  product_id: string;
  office: Office;
  product: Product;
  created_by: {
    id: string;
    name: string;
  }
};

const RealtorViewOfficeProduct = () => {
  const { productId } = useParams<{ productId: string }>();
  const { officeId } = useParams<{ officeId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [productImages, setProductImages] = useState<any[]>([]);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [office, setOffice] = useState<Office | null>(null);
  const [product, setProduct] = useState<Product>();
  const [officeProduct, setOfficeProduct] = useState<OfficeInventoryObj | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  useEffect(() => {
    const fetchOffice = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/offices/${officeId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch office data");
        }

        const data = await response.json();
        console.log("Fetched Office Data:", data);

        if (data) {
          setOffice(data);
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching office data:", error);
        setError("Failed to fetch office data.");
      } finally {
        setLoading(false);
      }
    };

    if (!office) {
      fetchOffice();
    }
  }, [officeId, office, token]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch product data");
        }

        const data = await response.json();
        console.log("Fetched Product Data:", data);

        if (data) {
          setProductImages(data.product.images ? data.product.images : []);
          setOfficeProduct(data);
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching product data:", error);
        setError("Failed to fetch product data.");
      } finally {
        setLoading(false);
      }
    };

    if (!product) {
      fetchProduct();
    }
  }, [productId, product, token]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/brands`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setBrands(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [token]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/categories`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [token]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "products");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.onload = () => {
        console.log("xhr:", xhr);
        if (xhr.readyState === 4) {
          const response = JSON.parse(xhr.responseText);
          console.log("upload response:", response);

          // Construct the full image URL using the base URL and `path` field
          const imageUrl = `${response.path}`;
          resolve(imageUrl);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: officeProduct?.product?.name || "",
      description: officeProduct?.product?.description || "",
      brand: officeProduct?.product?.brand || "",
      category: officeProduct?.product?.category || "",
      item_condition: officeProduct?.product?.item_condition || "",
      length: officeProduct?.product?.package_dimensions?.length || 0,
      height: officeProduct?.product?.package_dimensions?.height || 0,
      width: officeProduct?.product?.package_dimensions?.width || 0,
      weight: officeProduct?.product?.package_dimensions?.weight || 0,
      threshold_qty: officeProduct?.product?.threshold_qty || 0,
      installer: "",
      notes: officeProduct?.product?.notes || "",
      images: null,
      imageUrl: product?.image?.url || "", // Changed from null to empty string
      statuss: product?.status || "Active",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(50, "Name cannot be longer than 50 characters")
        .required("Name is required"),
      statuss: Yup.string().required("Status is required"),
      // image: Yup.mixed().test(
      //   "fileType",
      //   "Unsupported file format",
      //   (value) => {
      //     if (!value) return true; // Image is optional
      //     return value instanceof File
      //       ? ["image/jpeg", "image/png", "image/jpg"].includes(value.type)
      //       : false;
      //   }
      // ),
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(true);

    },
  });
  const idOf = (x?: any): string => (typeof x === "string" ? x : (x?.url ?? "")) || "";
  const buildImageUrl = (raw?: string) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    const path = String(raw).replace(/\\/g, "/").replace(/^\/+/, "");
    return `${base}/${encodeURI(path)}`;
  };

  const productImageIds = React.useMemo(
    () => (productImages || []).map((img: any) => idOf(img)).filter(Boolean),
    [productImages]
  );

  const dataForMap = React.useMemo(() => {
    if (!productImageIds.length) return [];
    return [{ product: { variants: [{ images: productImageIds.map((url) => ({ url })) }] } }];
  }, [productImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  // 🔧 bump when map content changes (even if the object ref is stable)
  const mapVersion = React.useMemo(
    () => (imageUrlMap ? Object.keys(imageUrlMap).length : 0),
    [imageUrlMap]
  );

  // ✅ will re-run when productImages change OR when map gets populated
  const resolvedImages = React.useMemo(
    () =>
      (productImages || []).map((img: any) => {
        const id = idOf(img);
        return {
          src: getImageSrc(id, imageUrlMap) || buildImageUrl(id) || imageCustomSquare,
          alt: img?.alt_text || "",
        };
      }),
    [productImages, mapVersion]   // <-- changed here
  );
  const galleryImages = resolvedImages.length
    ? resolvedImages
    : [{ src: imageCustomSquare, alt: "No image" }];


  //   if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content">
      <div className="d-flex justify-content-between">
        <h2 className="ps-4">Product Details</h2>
        {/* <Link
          to={`${baseRoute}/officeProduct/${officeId}/editOfficeProduct/${productId}`}
          className="border-0 p-0"
        >
          <i className="ri-pencil-fill fs-24"></i>
        </Link> */}
      </div>
      <div className="row justify-content-center">
        <div className="col-md-12 col-lg-12 col-xl-12">
          <div className="card mt-1 card-bg-fill">
            <div className="card-body p-4">
              <div className="p-2 mt-1">
                <div className="row">
                  <div className="col-lg-5">
                    <div className="gall-card shadow-lg">
                      <div className="gall-main">
                        <Swiper
                          onSwiper={(s: any) => (swiperRef.current = s)}
                          onSlideChange={(s: any) => setActiveIndex(s.activeIndex)}
                          slidesPerView={1}
                          spaceBetween={8}
                          className="gall-swiper"
                        >
                          {galleryImages.map((img, i) => (
                            <SwiperSlide key={i}>
                              <img
                                className="gall-img"
                                src={img.src}
                                alt={img.alt || `Image ${i + 1}`}
                                onError={(e) => {
                                  e.currentTarget.src = imageCustomSquare;
                                }}
                              />
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </div>

                      <div className="gall-thumbs">
                        {galleryImages.map((img, i) => (
                          <button
                            key={i}
                            type="button"
                            className={`gall-thumb ${i === activeIndex ? "is-active" : ""}`}
                            onClick={() => swiperRef.current?.slideTo(i)}
                          >
                            <img
                              src={img.src}
                              alt={`Thumb ${i + 1}`}
                              onError={(e) => {
                                e.currentTarget.src = imageCustomSquare;
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-7">
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">Office</label>
                        <input
                          className="form-control"
                          id="office"
                          name="office"
                          value={officeProduct?.office?.officeName}
                          readOnly
                        />
                      </div>
                      <div className="col-md-8 mb-3">
                        <label className="form-label table-cells">
                          Product Name{" "}

                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={officeProduct?.product?.name}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">
                          SKU <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="sku"
                          name="sku"
                          value={officeProduct?.product?.sku}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">Brand</label>
                        <input
                          className="form-control"
                          id="brand"
                          name="brand"
                          value={officeProduct?.product?.brand}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">Category</label>
                        <input
                          className="form-control"
                          id="category"
                          name="category"
                          value={officeProduct?.product?.category}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">
                          Item Condition
                        </label>
                        <input
                          className="form-control"
                          id="item_condition"
                          name="item_condition"
                          value={officeProduct?.product?.item_condition}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">
                          Quantity{" "}

                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="threshold_qty"
                          name="threshold_qty"
                          value={officeProduct?.product?.variants?.[0]?.inventory?.[0]?.available || 0}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">
                          Threshold Quantity{" "}

                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="threshold_qty"
                          name="threshold_qty"
                          value={officeProduct?.product?.threshold_qty}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">
                          Status
                        </label>
                        <input
                          className="form-control"
                          id="statuss"
                          name="statuss"
                          value={officeProduct?.product?.status}
                          disabled
                          readOnly
                        />
                      </div>
                      <div className="col-md-12 mb-3">
                        <label className="form-label table-cells">
                          Product Description{" "}

                        </label>
                        <textarea
                          className="form-control product-description-textarea"
                          id="description"
                          name="description"
                          value={officeProduct?.product?.description}
                          rows={4}
                          readOnly
                        ></textarea>
                      </div>
                      <div className="col-md-12 mb-3">
                        <label className="form-label table-cells">
                          Dimensions/Measurements (Inches)
                        </label>
                        <div className="row">
                          <div className="col-md-4" id="row-overlap">
                            <input
                              type="text"
                              className="form-control mt-3 ms-3"
                              id="length"
                              name="length"
                              value={officeProduct?.product?.package_dimensions?.length}
                              readOnly
                            />
                          </div>
                          <div className="col-md-4">
                            <input
                              type="text"
                              className="form-control"
                              id="height"
                              name="height"
                              value={officeProduct?.product?.package_dimensions?.height}
                              readOnly
                            />
                          </div>
                          <div className="col-md-4">
                            <input
                              type="text"
                              className="form-control"
                              id="width"
                              name="width"
                              value={officeProduct?.product?.package_dimensions?.width}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">
                          Weight(in gms)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="weight"
                          name="weight"
                          value={officeProduct?.product?.package_dimensions?.weight}
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="row mb-3 rcust-margin-4">
                        <div className="col-md-12 d-flex justify-content-between  ms-3" id="row-overlap">
                          <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="btn back-btn surface-btn fw-bold fs-16"
                          >
                            <i className="bi bi-chevron-left me-2" />
                            Back
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtorViewOfficeProduct;
